import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { InitiateTransactionParams } from './types';
import { Stripe as StripePlugin, Stripe } from '@capacitor-community/stripe';
import { environment } from '../../../enviroments';
import { StripePaymentsContext } from './StripePaymentsContext';
import { isPlatform } from '@ionic/react';

type StripePaymentsProviderType = {
  children: ReactNode;
};

export const StripePaymentProvider: FC<StripePaymentsProviderType> = ({ children }): JSX.Element => {
  const [countryCode] = useState('EE');

  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
  const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(false);

  const initiateApplePayTransaction = useCallback(
    async ({ paymentSummaryItems, paymentIntentClientSecret, paymentIntentCurrency }: InitiateTransactionParams) => {
      await Stripe.createApplePay({
        paymentIntentClientSecret: paymentIntentClientSecret,
        paymentSummaryItems: paymentSummaryItems,
        countryCode,
        currency: paymentIntentCurrency,
        merchantIdentifier: environment.APPLE_PAY_MERCHANT_ID,
      });

      const { paymentResult } = await Stripe.presentApplePay();

      return paymentResult;
    },
    [countryCode]
  );

  const initiateGooglePayTransaction = useCallback(
    async ({ paymentSummaryItems, paymentIntentClientSecret, paymentIntentCurrency }: InitiateTransactionParams) => {
      await Stripe.createGooglePay({
        paymentIntentClientSecret: paymentIntentClientSecret,
        paymentSummaryItems: paymentSummaryItems,
        countryCode,
        currency: paymentIntentCurrency,
      });

      const { paymentResult } = await Stripe.presentGooglePay();

      return paymentResult;
    },
    [countryCode]
  );

  useEffect(() => {
    (async () => {
      try {
        await StripePlugin.initialize({
          publishableKey: environment.STRIPE_PUBLIC_KEY,
        });

        if (isPlatform('hybrid')) {
          Stripe.isApplePayAvailable().then(() => {
            setIsApplePayAvailable(true);
          });
          Stripe.isGooglePayAvailable().then(() => {
            setIsGooglePayAvailable(true);
          });
        }
      } catch (error) {
        console.error('StripeProvider: Error initializing Stripe:', error);
      }
    })();
  }, []);

  return (
    <StripePaymentsContext.Provider
      value={{
        isApplePayAvailable,
        isGooglePayAvailable,
        initiateApplePayTransaction,
        initiateGooglePayTransaction,
        countryCode,
      }}
    >
      {children}
    </StripePaymentsContext.Provider>
  );
};
